<template>
  <div class="chat-input-container">
    <!-- 이미지 미리보기 영역 -->
    <div v-if="imagePreviews.length > 0" class="image-preview-container">
      <div class="image-previews">
        <div v-for="(preview, index) in imagePreviews" :key="index" class="image-preview">
          <img :src="preview" alt="이미지 미리보기" />
          <button class="remove-image-btn" @click="removeImage(index)">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div v-if="imagePreviews.length > 0" class="clear-all-btn-container">
          <button class="clear-all-btn" @click="removeAllImages">
            모든 이미지 제거
          </button>
        </div>
      </div>
    </div>

    <div class="input-wrapper">
      <!-- 이미지 첨부 버튼 -->
      <button class="attach-button" @click="triggerFileInput" title="이미지 첨부">
        <i class="ri-image-line"></i>
      </button>
      <input
        type="file"
        ref="fileInput"
        @change="handleFileChange"
        accept="image/*"
        multiple
        style="display: none"
      />

      <textarea
        ref="messageInput"
        v-model="message"
        placeholder="메시지를 입력하세요... (이미지 붙여넣기 가능)"
        @keydown.enter.prevent="handleEnterKey"
        @input="autoResize"
        @paste="handlePaste"
        :disabled="isLoading"
        class="message-input"
      ></textarea>

      <button
        class="send-button"
        @click="sendMessage"
        :disabled="(!message.trim() && imagePreviews.length === 0) || isLoading"
        title="메시지 전송"
      >
        <i class="ri-send-plane-fill"></i>
      </button>
    </div>

    <div class="input-footer d-flex justify-content-between">
      <span v-if="imagePreviews.length > 0" class="image-count">
        첨부된 이미지: {{ imagePreviews.length }}개
      </span>
      <span class="input-hint">Enter 키를 눌러 전송, Shift+Enter로 줄바꿈, Ctrl+V로 이미지 붙여넣기</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIChatInput',
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      message: '',
      imageFiles: [], // 여러 이미지 파일을 저장할 배열
      imagePreviews: [] // 여러 이미지 미리보기를 저장할 배열
    };
  },
  mounted() {
    this.$refs.messageInput.focus();
  },
  methods: {
    sendMessage() {
      if ((!this.message.trim() && this.imageFiles.length === 0) || this.isLoading) return;

      // 이미지와 메시지 모두 전송
      this.$emit('send-message', {
        text: this.message,
        images: [...this.imageFiles] // 배열 복사본 전달
      });

      // 입력 초기화
      this.message = '';
      this.imageFiles = [];
      this.imagePreviews = [];

      // 입력창 높이 초기화
      this.$refs.messageInput.style.height = 'auto';

      // 포커스 유지
      this.$nextTick(() => {
        this.$refs.messageInput.focus();
      });
    },

    // 파일 입력 트리거
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // 파일 변경 처리
    handleFileChange(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // 여러 파일 처리
      Array.from(files).forEach(file => {
        this.processImageFile(file);
      });

      // 파일 입력 초기화 (같은 파일 다시 선택 가능하도록)
      this.$refs.fileInput.value = '';
    },

    // 이미지 파일 처리 메소드
    processImageFile(file) {
      // 이미지 파일만 허용
      if (!file.type.match('image.*')) {
        alert('이미지 파일만 첨부할 수 있습니다.');
        return;
      }

      // 파일 크기 제한 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('5MB 이하의 이미지만 첨부할 수 있습니다.');
        return;
      }

      // 최대 5개 이미지로 제한
      if (this.imageFiles.length >= 5) {
        alert('최대 5개까지 이미지를 첨부할 수 있습니다.');
        return;
      }

      // 이미지 파일 배열에 추가
      this.imageFiles.push(file);

      // 이미지 미리보기 생성
      const reader = new FileReader();
      reader.onload = e => {
        this.imagePreviews.push(e.target.result);
      };
      reader.readAsDataURL(file);
    },

    // 붙여넣기 이벤트 처리
    handlePaste(event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      let hasImage = false;

      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          // 이미지가 붙여넣기되면 파일로 변환
          const blob = item.getAsFile();
          this.processImageFile(blob);
          hasImage = true;
          break; // 첫 번째 이미지만 처리
        }
      }

      // 이미지가 있었다면 기본 붙여넣기 동작 방지
      if (hasImage) {
        event.preventDefault();
        event.stopPropagation();
      }
    },

    // 특정 이미지 제거
    removeImage(index) {
      this.imageFiles.splice(index, 1);
      this.imagePreviews.splice(index, 1);
    },

    // 모든 이미지 제거
    removeAllImages() {
      this.imageFiles = [];
      this.imagePreviews = [];
      this.$refs.fileInput.value = '';
    },

    handleEnterKey(event) {
      // Shift+Enter는 줄바꿈
      if (event.shiftKey) return;

      // Enter만 누르면 메시지 전송
      this.sendMessage();
    },

    autoResize() {
      const textarea = this.$refs.messageInput;

      // 높이 초기화
      textarea.style.height = 'auto';

      // 스크롤 높이에 맞게 조정 (최대 150px)
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  }
};
</script>

<style scoped>
.chat-input-container {
  padding: 16px 20px;
  background-color: #fff;
  border-top: 1px solid #e5e7eb;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  background-color: #f3f4f6;
  border-radius: 24px;
  padding: 10px 16px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.input-wrapper:focus-within {
  background-color: #fff;
  box-shadow: 0 0 0 2px #3b82f6, 0 2px 4px rgba(0, 0, 0, 0.1);
}

.message-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 15px;
  line-height: 1.5;
  padding: 8px 0;
  max-height: 150px;
  resize: none;
  outline: none;
  color: #1f2937;
  font-family: inherit;
}

.message-input::placeholder {
  color: #9ca3af;
}

.message-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.attach-button {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.attach-button:hover {
  color: #3b82f6;
}

.send-button {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 10px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.send-button:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.send-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.send-button i {
  font-size: 18px;
}

.input-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
  padding: 0 8px;
}

.input-hint {
  font-size: 12px;
  color: #9ca3af;
}

/* 이미지 미리보기 스타일 */
.image-preview-container {
  margin-bottom: 10px;
}

.image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.image-preview {
  position: relative;
  display: inline-block;
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.remove-image-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.remove-image-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.clear-all-btn-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
}

.clear-all-btn {
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-all-btn:hover {
  background-color: #e5e7eb;
  color: #1f2937;
}

.image-count {
  font-size: 12px;
  color: #4b5563;
}
</style>
